import { z } from 'zod';
import { LowercaseUuidSchema, PaginationQuerySchema, createPaginatedResponseSchema, saPhoneNumberSchema, saVatNumberSchema, writeWarningsFields } from './common.js';
import { nullableText } from '../sanitised-text.js';
import { EntitySourceSchema } from './client.js';
import { BusinessEntitySchema, businessEntityIdentityTaxInput } from './account.js';
// =============================================================================
// Supplier
// =============================================================================
/**
 * A known invoicing-sender email address for a supplier. Mirrors
 * `ClientEmailDtoSchema`, but exposes `source` directly rather than deriving a
 * `type` - there is no AI-detected row to hide from this DTO yet. See the
 * backend `SupplierEmailDto` docblock for the full rationale.
 */
export const SupplierEmailDtoSchema = z.object({
    id: z.string(),
    supplierId: z.string(),
    email: z.string(),
    isPrimary: z.boolean(),
    source: EntitySourceSchema,
    name: z.string().nullable(),
    createdAt: z.string(),
});
export const SupplierEmailAddressSchema = z.email('Enter a valid email address').max(254, 'Email must be 254 characters or less');
// Mirrors ClientEmailContactNameSchema.
export const SupplierEmailContactNameSchema = nullableText(200, 'Name must be 200 characters or less');
// Resource-creating POST: idempotencyKey is required per the platform-wide
// convention (`apps/backend/platform/src/api/CLAUDE.md` "Idempotency key on
// POST endpoints"). AddClientEmailBodySchema predates that convention and was
// not migrated when this file was written; do not copy its omission.
export const AddSupplierEmailBodySchema = z.object({
    email: SupplierEmailAddressSchema,
    name: SupplierEmailContactNameSchema,
    idempotencyKey: z.string().min(1),
});
// Only isPrimary: true is accepted; false is rejected by schema validation
// (400). Mirrors UpdateClientEmailBodySchema. The MCP route below injects
// isPrimary: true via `overrides.body`, so the agent never sees this literal.
/* eslint-disable zod-compat/no-zod-boolean-literal */
export const UpdateSupplierEmailBodySchema = z.object({
    isPrimary: z.literal(true),
});
export const ListSupplierEmailsResponseSchema = z.object({
    emails: z.array(SupplierEmailDtoSchema),
});
export const SupplierEmailResponseSchema = z.object({
    email: SupplierEmailDtoSchema,
});
export const SupplierSchema = z.object({
    id: z.string(),
    name: z.string(),
    phone: z.string().nullable(),
    // `Supplier` carries no `email` column (see the backend `SupplierEmail`
    // Prisma docblock) - this is the primary row from `emails` below, mirroring
    // `ClientSchema.primaryEmail`. Null when the supplier has no emails yet.
    primaryEmail: z.string().nullable(),
    // The supplier's linked BusinessEntity (1:1) is exposed as a nested object,
    // mirroring how Client and Account surface their BusinessEntity. The entity
    // carries the VAT number (and address, etc.). `businessEntityId` is null and
    // `businessEntity` is null when the supplier has no linked entity.
    businessEntityId: z.string().nullable(),
    businessEntity: BusinessEntitySchema.nullable().optional(),
    // Our account number with this supplier, as printed on their documents under
    // "Account" / "Account No.". Belongs to the trading relationship rather than
    // to any one invoice, so it lives here and not on SupplierInvoice. Null for
    // every supplier created before this field shipped.
    customerAccountNumber: z.string().nullable(),
    // Known invoicing-sender addresses (see SupplierEmailDtoSchema), mirroring
    // how ClientSchema nests `emails`. Used for both the list and single-supplier
    // read shapes; `listSuppliers`' MCP `defaultFields` omits it by default the
    // same way `listClients` does, since it is structural/per-supplier detail
    // rather than a list-scanning field.
    emails: z.array(SupplierEmailDtoSchema),
    source: EntitySourceSchema,
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Business-entity fields a supplier write may carry inline. The supplier domain
 * folds these into the linked BusinessEntity (creating it on the fly) rather
 * than exposing a separate upsert route, because the supplier is most often
 * created inline from the expense form and a second round trip there would let
 * a supplier land without its address. Widths match
 * `UpsertClientBusinessEntityInputSchema` so the two entity writers agree.
 *
 * `addressCountry` is an ISO-2 code; BusinessEntity defaults it to 'ZA'.
 */
const supplierBusinessEntityFields = {
    vatNumber: saVatNumberSchema,
    ...businessEntityIdentityTaxInput,
    addressLine1: nullableText(300),
    addressLine2: nullableText(300),
    addressCity: nullableText(100),
    addressProvince: nullableText(100),
    addressPostalCode: nullableText(20),
    addressCountry: z.string().max(100).optional(),
};
export const CreateSupplierInputSchema = z.object({
    name: z.string().min(1, 'Supplier name is required').max(200, 'Name must be 200 characters or less'),
    // VAT number and address are stored on the supplier's linked BusinessEntity
    // (created on the fly) and read back on the GET response. `saVatNumberSchema`
    // is shared with client / account so a VAT typed or pasted with spaces
    // ('4990 123 456') is stored canonical ('4990123456') and matches the extraction
    // VAT lookup. It is nullable and optional at source: an empty string sent
    // by the form mappers (toCreateSupplierInput / toUpdateSupplierInput)
    // collapses to `null` rather than being rejected.
    ...supplierBusinessEntityFields,
    // Deliberately NO bank fields here. Where a supplier asks to be paid is
    // recorded only through the supplier-scoped bank-account resource
    // (`POST /accounts/:accountId/suppliers/:supplierId/bank-accounts`), the
    // single gated write path for that data (see the "HARD invariant" section of
    // `apps/backend/platform/src/domains/supplier-invoice/README.md`). A second create-time door onto the same
    // `BankAccount` model would duplicate that write path under a weaker gate -
    // create the supplier, then record its banking details as an explicit
    // second step.
    phone: saPhoneNumberSchema.optional().nullable(),
    // CREATE-only sugar - `Supplier` has no `email` column. When supplied, the
    // service writes it as the new supplier's primary `SupplierEmail` row in
    // the same call, so the two flows that create a supplier with an email in
    // one round trip (the expense form's inline "new supplier" quick-create,
    // and invoice extraction) do not need a second write. Deliberately absent
    // from `UpdateSupplierInputSchema` below - once a supplier exists, its
    // addresses are managed through the email sub-resource routes
    // (`POST/PATCH/DELETE .../suppliers/:supplierId/emails`), not this input.
    email: z.email('Enter a valid email address').max(254, 'Email must be 254 characters or less').optional().nullable(),
    customerAccountNumber: nullableText(100, 'Account number must be 100 characters or less'),
    source: EntitySourceSchema.optional(),
    idempotencyKey: z.string().min(1),
});
export const UpdateSupplierInputSchema = z.object({
    name: z.string().min(1, 'Supplier name is required').max(200, 'Name must be 200 characters or less').optional(),
    ...supplierBusinessEntityFields,
    phone: saPhoneNumberSchema.optional().nullable(),
    customerAccountNumber: nullableText(100, 'Account number must be 100 characters or less'),
});
/**
 * Response for both `POST .../suppliers` and `PATCH .../suppliers/:supplierId`:
 * the created or updated `Supplier`, plus the shared `warnings` /
 * `warningDetails` pair a write carrying `vatNumber` can populate (see
 * `vatCheckDigitWarning` in `common.js`).
 */
export const MutateSupplierResponseSchema = SupplierSchema.extend(writeWarningsFields);
export const ListSuppliersQuerySchema = z.object({
    includeArchived: z.enum(['true', 'false']).optional(),
    archivedOnly: z.enum(['true', 'false']).optional(),
    ids: z.array(LowercaseUuidSchema).max(100).optional(),
    search: z.string().max(200).optional(),
}).extend(PaginationQuerySchema.shape);
export const SupplierListResponseSchema = createPaginatedResponseSchema(SupplierSchema);
export const DeleteSupplierResponseSchema = z.object({
    success: z.boolean(),
});
